import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTestimonialByToken, useSubmitTestimonial } from "@/hooks/useTestimonials";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Star, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import logoUrl from "@/assets/brand/simbolo.svg";
import SEOMeta from "@/components/SEOMeta";
import { LoadErrorState } from "@/components/shared/LoadErrorState";

const RATING_LABELS: Record<number, string> = {
  1: "Ruim",
  2: "Regular",
  3: "Bom",
  4: "Muito bom",
  5: "Excelente",
};

function FeedbackPageView() {
  const { token } = useParams();
  const { data: record, isLoading: loading, isError, refetch } = useTestimonialByToken(token);
  const submitTestimonial = useSubmitTestimonial();
  const [submitted, setSubmitted] = useState(false);
  const [quote, setQuote] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [rating, setRating] = useState(5);
  const [lgpdConsent, setLgpdConsent] = useState(false);
  const saving = submitTestimonial.isPending;

  // Pré-preenche nome/cargo pedidos pelo operador; já respondido → tela de obrigado.
  useEffect(() => {
    if (!record) return;
    setName(record.author_name || "");
    setRole(record.author_role || "");
    if (record.submitted_at) setSubmitted(true);
  }, [record]);

  const submit = async () => {
    if (!token) return;
    try {
      await submitTestimonial.mutateAsync({
        token,
        quote,
        author_name: name,
        author_role: role,
        rating,
      });
      setSubmitted(true);
    } catch {
      toast.error("Não foi possível enviar seu depoimento. Tente novamente ou fale com Eliveuton.");
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Network/RPC failure ≠ invalid link: don't tell the client the link died.
  if (isError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <Card className="w-full max-w-md rounded-2xl border-border shadow-card">
          <LoadErrorState title="Não foi possível abrir o link agora." onRetry={() => refetch()} />
        </Card>
      </div>
    );
  }

  if (!record) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <Card className="w-full max-w-md rounded-2xl border-border shadow-card">
          <CardContent className="p-8 text-center">
            <XCircle className="mx-auto mb-3 h-12 w-12 text-destructive" />
            <h1 className="font-semibold text-foreground">Link inválido ou expirado</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Este link de depoimento não existe ou já venceu. Peça um novo link para Eliveuton.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <Card className="w-full max-w-md rounded-2xl border-ok bg-ok-soft text-center shadow-card">
          <CardContent className="space-y-3 p-8">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-ok text-ok-foreground">
              <CheckCircle2 className="h-7 w-7" />
            </span>
            <h1 className="font-display text-xl font-bold text-foreground">Obrigado!</h1>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Seu depoimento foi enviado e está em revisão. Publico depois da sua aprovação.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto max-w-[580px] space-y-4">
        <div className="flex items-center gap-3">
          <img src={logoUrl} alt="Eliveuton Souza" className="h-10 w-10 object-contain" />
          <div>
            <p className="font-semibold text-foreground">Eliveuton Souza</p>
            <p className="text-xs text-muted-foreground">Desenvolvimento Web & Automação com IA</p>
          </div>
        </div>

        <Card className="rounded-[18px] border-border shadow-card">
          <CardHeader className="p-[30px] pb-0">
            <CardTitle className="font-display text-[22px] font-bold tracking-[-.03em]">
              Como foi trabalhar comigo?
            </CardTitle>
            <p className="pb-2 text-sm leading-relaxed text-muted-foreground">
              Leva menos de dois minutos. Publico depois da sua aprovação — e você pode pedir para
              tirar quando quiser.
            </p>
          </CardHeader>
          <CardContent className="space-y-4 p-[30px] pt-2">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label
                  htmlFor="feedback-seu-nome"
                  className="text-xs font-semibold text-muted-foreground"
                >
                  Seu nome
                </Label>
                <Input
                  id="feedback-seu-nome"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Como você se chama"
                />
              </div>
              <div className="space-y-1.5">
                <Label
                  htmlFor="feedback-cargo-empresa"
                  className="text-xs font-semibold text-muted-foreground"
                >
                  Cargo / Empresa
                </Label>
                <Input
                  id="feedback-cargo-empresa"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  placeholder="Diretora · Empresa"
                />
              </div>
            </div>

            <div className="space-y-2">
              <span
                id="feedback-rating-label"
                className="text-xs font-semibold text-muted-foreground"
              >
                Sua nota
              </span>
              <div
                className="flex items-center gap-1.5"
                role="group"
                aria-labelledby="feedback-rating-label"
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setRating(n)}
                    className="rounded-md transition-transform hover:scale-110"
                    aria-label={`${n} estrela${n > 1 ? "s" : ""}`}
                  >
                    <Star
                      className={`h-[30px] w-[30px] ${n <= rating ? "fill-warn text-warn" : "text-border"}`}
                    />
                  </button>
                ))}
                <span className="ml-2 text-[12.5px] font-semibold text-muted-foreground">
                  {RATING_LABELS[rating]}
                </span>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label
                htmlFor="feedback-depoimento"
                className="text-xs font-semibold text-muted-foreground"
              >
                Depoimento
              </Label>
              <Textarea
                id="feedback-depoimento"
                rows={5}
                value={quote}
                onChange={(e) => setQuote(e.target.value)}
                placeholder="O que mudou na sua rotina depois do projeto? Um exemplo concreto vale mais que elogio."
              />
            </div>

            <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/40 p-3.5">
              <Checkbox
                id="lgpd-consent"
                checked={lgpdConsent}
                onCheckedChange={(v) => setLgpdConsent(!!v)}
                className="mt-0.5"
              />
              <label
                htmlFor="lgpd-consent"
                className="cursor-pointer text-xs leading-relaxed text-muted-foreground"
              >
                Concordo com a publicação deste depoimento e o tratamento dos meus dados, conforme a{" "}
                <Link
                  to="/politica-de-privacidade"
                  className="text-primary underline hover:no-underline"
                >
                  Política de Privacidade
                </Link>
                .
              </label>
            </div>

            <Button
              onClick={submit}
              disabled={!quote.trim() || !name.trim() || !lgpdConsent || saving}
              className="h-12 w-full rounded-xl text-base font-semibold"
            >
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Enviar depoimento
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Página por token: nunca indexar nem gerar preview com dado do cliente
// (docs/specs/standards/pages-and-routing.md, regra 2).
export default function FeedbackPage() {
  return (
    <>
      <SEOMeta
        title="Depoimento"
        description="Página privada para enviar seu depoimento."
        noindex
      />
      <FeedbackPageView />
    </>
  );
}
